import React from 'react';
import { Skeleton } from 'fronton-react';
import {
  getProductId,
  isWebSellable,
  isLaminateCalculator,
  getPrimaryPrice,
  getPrimaryPriceUnitSlug,
  getAdditionalPriceUnitSlug,
} from 'features/product-info/ducks/selectors';
import {
  getPrimaryPriceUnitName,
  getIsPricesSwapped,
  getSecondPrice,
  SecondPrice,
} from 'features/product-info';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  BasketItemFlow,
  getMeasurementSettings,
  BasketFlowActions,
  getProductQuantityAccordingToSelectedServices,
  BasketItem,
  useBasketUpdate,
  getBasketLoading,
} from 'features/basket';
import { ConnectedProps } from 'types/utils';
import { getGoodsSupplyCase } from 'features/goods-supply/selectors';
import {
  getLaminatConfigurationPriceCoefficient,
  getLaminateConfigurationArea,
} from 'features/configurations/ducks/selectors';
import { RootReducer } from 'store/root-reducer';
import { getIsBot } from 'common/selectors';
import { PriceMainProps } from 'common/components/product-price-view/types';
import { AddToBasketMappedState } from './types';
import { ButtonWithCounter } from './button-with-counter';
import { ButtonWithoutCounter } from './button-without-counter';

export type AddToBasketProps = ConnectedProps<AddToBasketMappedState, typeof mapDispatchToProps> &
  React.HTMLAttributes<HTMLDivElement>;

export const AddToBasket: React.FC<AddToBasketProps> = ({
  onBasketChange,
  onBasketRemove,
  initBasket,
  updateBasket,
  className = '',
  classnameCounter = '',
  productId,
  quantity,
  supplyCase,
  isSellable,
  measurementSettings,
  mainUom,
  mainPriceUnitSlug,
  additionalPriceUnitSlug,
  configurationArea,
  isLaminateCalc,
  isSwapPrice,
  firstPrice,
  secondPrice,
  size = 'l',
  isStickyPanel,
  isBot,
  isBasketLoading,
  configurationPriceCoefficient,
  ...htmlProps
}) => {
  useBasketUpdate(initBasket, updateBasket);

  let content: React.ReactNode = null;

  if (isSellable) {
    const inBasket = Boolean(quantity);

    content = (measurementSettings || isBot) && (
      <div className={className} {...htmlProps}>
        {inBasket ? (
          <ButtonWithCounter
            classnameCounter={classnameCounter}
            isStickyPanel={isStickyPanel}
            productId={productId}
            measurementSettings={measurementSettings}
            onBasketChange={onBasketChange}
            onBasketRemove={onBasketRemove}
            mainUom={mainUom}
            quantity={quantity}
            isSwapPrice={isSwapPrice}
            size={size}
            secondPrice={secondPrice}
          />
        ) : (
          <ButtonWithoutCounter
            onBasketChange={onBasketChange}
            measurementSettings={measurementSettings}
            productId={productId}
            size={size}
            secondPrice={secondPrice}
            firstPrice={firstPrice}
            isStickyPanel={isStickyPanel}
            isLaminateCalc={isLaminateCalc}
            additionalPriceUnitSlug={additionalPriceUnitSlug}
            mainPriceUnitSlug={mainPriceUnitSlug}
            configurationArea={configurationArea}
            configurationPriceCoefficient={configurationPriceCoefficient}
          />
        )}
      </div>
    );

    content = (
      <Skeleton variant="control" width="100%" showLoading={!isBot && isBasketLoading}>
        {content}
      </Skeleton>
    );

    if (supplyCase !== null && ['CASE_1', 'CASE_2', 'CASE_5'].includes(supplyCase)) {
      content = null;
    }
  }

  return <>{content}</>;
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  onBasketChange: (productId: BasketItemFlow['productId'], flowQty: BasketItemFlow['flowQty']) =>
    dispatch(BasketFlowActions.UPDATE_ITEM({ productId, flowQty })),
  onBasketRemove: (productId: string) => dispatch(BasketFlowActions.RESET_ITEM({ productId })),
  initBasket: () => dispatch(BasketFlowActions.LOAD()),
  updateBasket: (basket: BasketItem[]) => dispatch(BasketFlowActions.UPDATE(basket)),
});

const mapStoreToProps = (store: RootReducer, props: AddToBasketProps): AddToBasketMappedState => {
  const productId = getProductId(store) || '';
  const quantity = getProductQuantityAccordingToSelectedServices(productId)(store);
  const supplyCase = getGoodsSupplyCase(store);
  const isSellable = isWebSellable(store);
  const measurementSettings = getMeasurementSettings(store);
  const mainUom = props.uom ?? getPrimaryPriceUnitName(store) ?? '';
  const mainPriceUnitSlug = getPrimaryPriceUnitSlug(store);
  const additionalPriceUnitSlug = getAdditionalPriceUnitSlug(store);
  const configurationArea = getLaminateConfigurationArea(store);
  const isLaminateCalc = isLaminateCalculator(store);
  const isSwapPrice = props.isSwapPrice ?? getIsPricesSwapped(store) ?? false;
  const firstPrice = getPrimaryPrice(store) as PriceMainProps;
  const secondPrice = getSecondPrice(store) as SecondPrice;
  const isBot = getIsBot(store);
  const isBasketLoading = getBasketLoading(store);
  const configurationPriceCoefficient = getLaminatConfigurationPriceCoefficient(store);

  return {
    productId,
    quantity,
    supplyCase,
    isSellable,
    measurementSettings,
    mainUom,
    mainPriceUnitSlug,
    additionalPriceUnitSlug,
    configurationArea,
    isLaminateCalc,
    isSwapPrice,
    firstPrice,
    secondPrice,
    isBot,
    isBasketLoading,
    configurationPriceCoefficient,
  };
};

export const PdpAddToBasket = connect(mapStoreToProps, mapDispatchToProps)(AddToBasket);
